import { useState, useEffect } from 'react';
import { X, Shield, Settings, Check } from 'lucide-react';
import { useCookieConsent } from '../hooks/useCookieConsent';
import type { CookieCategory } from '../types';

interface CookieConsentBannerProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

const cookieDescriptions: Record<CookieCategory, { title: string; description: string }> = {
  essential: {
    title: 'Essenziell',
    description: 'Notwendige Cookies für die Grundfunktionen der Website. Können nicht deaktiviert werden.',
  },
  statistics: {
    title: 'Statistik',
    description: 'Helfen uns zu verstehen, wie Besucher mit der Website interagieren (z.B. Google Analytics).',
  },
  marketing: {
    title: 'Marketing',
    description: 'Werden verwendet, um Besuchern relevante Werbung anzuzeigen.',
  },
};

export function CookieConsentBanner({ forceOpen = false, onClose }: CookieConsentBannerProps) {
  const { 
    consent, 
    isLoaded, 
    showBanner, 
    acceptAll, 
    acceptEssentialOnly, 
    saveConsent,
  } = useCookieConsent();
  
  const [showSettings, setShowSettings] = useState(false);
  const [tempConsent, setTempConsent] = useState({
    essential: true,
    statistics: false,
    marketing: false,
  });

  const isOpen = forceOpen || (showBanner && !showSettings);
  const isSettingsOpen = forceOpen || showSettings;

  useEffect(() => {
    if (consent) {
      setTempConsent({
        essential: true,
        statistics: consent.statistics,
        marketing: consent.marketing,
      });
    }
  }, [consent]);

  if (!isLoaded) return null;

  const handleSaveSettings = () => {
    saveConsent(tempConsent);
    setShowSettings(false);
    onClose?.();
  };

  const handleAcceptAll = () => {
    acceptAll();
    setShowSettings(false);
    onClose?.();
  };

  const handleAcceptEssential = () => {
    acceptEssentialOnly();
    setShowSettings(false);
    onClose?.();
  };

  const toggleCategory = (category: Exclude<CookieCategory, 'essential'>) => {
    setTempConsent(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Main Banner
  if (isOpen && !isSettingsOpen) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-platin-200 animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
      >
        <div className="container-custom py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1 flex items-start gap-4">
              <div className="p-3 bg-brand-950 rounded-lg shrink-0">
                <Shield className="w-6 h-6 text-platin-200" />
              </div>
              <div>
                <h3 id="cookie-title" className="font-semibold text-lg text-brand-950 mb-1">
                  Ihre Datenschutz-Einstellungen
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung zu bieten. 
                  Essenzielle Cookies sind für den Betrieb erforderlich.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <button
                onClick={() => setShowSettings(true)}
                className="px-6 py-3 rounded-lg font-medium text-sm border border-platin-300 text-brand-950 hover:bg-platin-50 transition-colors flex items-center justify-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Einstellungen
              </button>
              <button
                onClick={handleAcceptEssential}
                className="px-6 py-3 rounded-lg font-medium text-sm border border-platin-300 text-brand-950 hover:bg-platin-50 transition-colors"
              >
                Nur Essenzielle
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 rounded-lg font-medium text-sm bg-brand-950 text-white hover:bg-brand-800 transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Settings Modal
  if (isSettingsOpen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-platin-200">
            <div className="flex items-center justify-between">
              <h3 id="cookie-settings-title" className="font-semibold text-xl text-brand-950">
                Cookie-Einstellungen
              </h3>
              <button
                onClick={() => {
                  setShowSettings(false);
                  onClose?.();
                }}
                className="p-2 hover:bg-platin-100 rounded-lg transition-colors"
                aria-label="Schließen"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <p className="text-gray-600 text-sm">
              Wählen Sie aus, welche Cookies Sie akzeptieren möchten. Ihre Einstellungen werden 
              lokal in Ihrem Browser gespeichert.
            </p>

            {/* Essential Cookies - Always On */}
            <div className="flex items-start gap-4 p-4 bg-platin-50 rounded-xl">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-semibold text-brand-950">
                    {cookieDescriptions.essential.title}
                  </span>
                  <span className="px-2 py-1 bg-brand-950 text-platin-200 text-xs font-medium rounded-full">
                    Immer aktiv
                  </span>
                </div>
                <p className="text-gray-600 text-sm">
                  {cookieDescriptions.essential.description}
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="w-12 h-6 bg-brand-950 rounded-full appearance-none cursor-not-allowed opacity-60"
                />
                <span className="absolute right-1.5 top-1.5 w-3 h-3 bg-platin-200 rounded-full pointer-events-none" />
              </div>
            </div>

            {/* Statistics Cookies */}
            <div className="flex items-start gap-4 p-4 border border-platin-200 rounded-xl hover:border-platin-300 transition-colors">
              <div className="flex-1">
                <h4 className="font-semibold text-brand-950 mb-2">
                  {cookieDescriptions.statistics.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {cookieDescriptions.statistics.description}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={tempConsent.statistics}
                  onChange={() => toggleCategory('statistics')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-platin-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-platin-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-platin-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-950"></div>
              </label>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start gap-4 p-4 border border-platin-200 rounded-xl hover:border-platin-300 transition-colors">
              <div className="flex-1">
                <h4 className="font-semibold text-brand-950 mb-2">
                  {cookieDescriptions.marketing.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {cookieDescriptions.marketing.description}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={tempConsent.marketing}
                  onChange={() => toggleCategory('marketing')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-platin-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-platin-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-platin-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-950"></div>
              </label>
            </div>
          </div>

          <div className="p-6 border-t border-platin-200 bg-platin-50 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowSettings(false)}
              className="px-6 py-3 rounded-lg font-medium text-sm border border-platin-300 text-brand-950 hover:bg-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-6 py-3 rounded-lg font-medium text-sm bg-brand-950 text-white hover:bg-brand-800 transition-colors"
            >
              Einstellungen speichern
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
